import { useState, useEffect, useMemo } from 'react';
import { objectsAPI, shiftsAPI } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Building2, MapPin, Clock, Calendar, Check, X, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function MyObjects() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [objects, setObjects] = useState([]);
  const [shifts, setShifts] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;

      const [objRes, shiftRes] = await Promise.all([
        objectsAPI.list({ is_active: true }),
        shiftsAPI.my(year, month)
      ]);

      setObjects(objRes.data || []);
      setShifts(shiftRes.data?.shifts || []);
    } catch (error) {
      console.error('Failed to load objects:', error);
      toast.error(t('msg.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  // Group shifts by object_id and find next upcoming shift
  const shiftsByObject = useMemo(() => {
    const now = new Date();
    const map = {};
    
    shifts.forEach(shift => {
      const objId = shift.object_id;
      if (!map[objId]) map[objId] = [];
      map[objId].push(shift);
    });

    // Sort each object's shifts by date and find first upcoming
    Object.keys(map).forEach(objId => {
      map[objId].sort((a, b) => new Date(a.date) - new Date(b.date));
      const upcoming = map[objId].find(s => new Date(s.date + 'T' + s.start_time) > now);
      map[objId] = upcoming || map[objId][map[objId].length - 1]; // upcoming or last
    });

    return map;
  }, [shifts]);

  const getGeofenceType = (obj) => {
    const geofence = obj.geofence || {};
    if (geofence.polygon && geofence.polygon.length >= 3) {
      if (geofence.mode === 'building') return t('myObjects.building');
      return t('myObjects.drawnZone');
    }
    return `${t('myObjects.radius')} ${geofence.radius || obj.radius || 100}m`;
  };

  const getRulesDisplay = (obj) => {
    const rules = obj.rules || {};
    const items = [];

    // Pause
    if (rules.pause_enabled) {
      items.push({
        icon: Check,
        text: `${t('myObjects.pauseAllowed')} ${rules.pause_max_minutes_per_shift ? `(${rules.pause_max_minutes_per_shift} ${t('myObjects.min')})` : ''}`,
        color: 'text-emerald-400'
      });
    } else {
      items.push({
        icon: X,
        text: t('myObjects.pauseNotAllowed'),
        color: 'text-rose-400'
      });
    }

    // Lunch
    if (rules.lunch_enabled) {
      const range = rules.lunch_min_duration && rules.lunch_max_duration 
        ? `(${rules.lunch_min_duration}-${rules.lunch_max_duration} ${t('myObjects.min')})`
        : '';
      items.push({
        icon: Check,
        text: `${t('myObjects.lunchAllowed')} ${range}`,
        color: 'text-emerald-400'
      });
    } else {
      items.push({
        icon: X,
        text: t('myObjects.lunchNotAllowed'),
        color: 'text-rose-400'
      });
    }

    return items;
  };

  const openInMaps = (obj) => {
    const lat = obj.geofence?.latitude || obj.latitude;
    const lng = obj.geofence?.longitude || obj.longitude;
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    window.open(url, '_blank');
  };

  const formatShift = (shift) => {
    if (!shift) return null;
    const date = new Date(shift.date);
    const dateStr = date.toLocaleDateString(language === 'et' ? 'et-EE' : language === 'ru' ? 'ru-RU' : 'en-US', {
      day: '2-digit',
      month: '2-digit'
    });
    return `${dateStr}, ${shift.start_time}-${shift.end_time}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <div className="h-10 w-10 rounded-full border-4 border-wp-medium/30 border-t-wp-light animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-objects-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-white font-heading flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-wp-medium/20 flex items-center justify-center">
            <Building2 className="h-6 w-6 text-wp-light" />
          </div>
          {t('myObjects.title')}
        </h1>
        <p className="text-wp-light/40 mt-2">{t('myObjects.subtitle')}</p>
      </div>

      {/* Objects List */}
      {objects.length === 0 ? (
        <div className="glass-card rounded-2xl p-12 text-center">
          <Building2 className="h-12 w-12 mx-auto text-wp-light/20 mb-4" />
          <p className="text-wp-light/40">{t('myObjects.noObjects')}</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {objects.map(obj => {
            const nextShift = shiftsByObject[obj.id];
            const rules = getRulesDisplay(obj);
            
            return (
              <div key={obj.id} className="glass-card rounded-2xl p-5 space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-semibold text-white truncate">{obj.name}</h3>
                    {obj.address && (
                      <p className="text-wp-light/60 text-sm mt-1 flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">{obj.address}</span>
                      </p>
                    )}
                  </div>
                </div>

                {/* Geofence Info */}
                <div className="flex items-center gap-2 text-sm text-wp-light/70">
                  <span className="px-2.5 py-1 rounded-lg bg-wp-dark/50 border border-wp-dark/30">
                    {getGeofenceType(obj)}
                  </span>
                  {obj.rules?.grace_period_minutes > 0 && (
                    <span className="px-2.5 py-1 rounded-lg bg-wp-dark/50 border border-wp-dark/30">
                      {t('myObjects.gracePeriod')}: {obj.rules.grace_period_minutes} {t('myObjects.min')}
                    </span>
                  )}
                </div>

                {/* Rules */}
                <div className="grid grid-cols-2 gap-3">
                  {rules.map((rule, idx) => (
                    <div key={idx} className={`flex items-center gap-2 text-sm ${rule.color}`}>
                      <rule.icon className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{rule.text}</span>
                    </div>
                  ))}
                </div>

                {/* Next Shift */}
                {nextShift && (
                  <div className="flex items-center gap-2 text-sm text-wp-light/80 pt-2 border-t border-wp-dark/30">
                    <Calendar className="h-4 w-4 text-wp-light/50" />
                    <span>{t('myObjects.nextShift')}:</span>
                    <span className="font-medium text-white">{formatShift(nextShift)}</span>
                  </div>
                )}

                {/* Open Map Button */}
                <button
                  onClick={() => openInMaps(obj)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-wp-medium/20 hover:bg-wp-medium/30 text-wp-light border border-wp-medium/20 transition-colors text-sm font-medium"
                >
                  <ExternalLink className="h-4 w-4" />
                  {t('myObjects.openMap')}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
